import React from 'react';

import _ from 'lodash';

import FileRow from '@/components/Common/FileRow';
import ImagePreview from '@/components/FormBuilder/components/Table/components/ImagePreview';
import { CoreEnums } from '@/utils/domain/constants';
import styles from './styles.less';

const toImagePreviewItem = (item) => {
  if (!item) return null;

  const contentType = item.contentType || 'image/png';
  const hasContent = typeof item.content === 'string' && item.content.length > 0;
  const rawFromContent = hasContent
    ? item.content.includes('base64,')
      ? _.split(item.content, 'base64,').pop()
      : item.content
    : null;

  // Prefer persisted URLs (saved files). SAS temporary often expires.
  const remoteUrl =
    (item.downloadUrl && /^https?:/i.test(item.downloadUrl) ? item.downloadUrl : null) ||
    (item.previewUrl && /^https?:/i.test(item.previewUrl) ? item.previewUrl : null) ||
    item.documentTemporaryUrl ||
    null;

  if (remoteUrl) {
    return {
      fileName: item.fileName,
      contentType,
      fileTypeClassification: item.fileTypeClassification,
      isBase64: false,
      downloadUrl: remoteUrl,
      previewUrl: remoteUrl,
      size: item.size,
      creator: item.creator,
    };
  }

  const isBase64 = item.isBase64 ?? !!rawFromContent;
  const payload = rawFromContent || item.previewUrl || item.downloadUrl;

  return {
    fileName: item.fileName,
    contentType,
    fileTypeClassification: item.fileTypeClassification,
    isBase64,
    downloadUrl: payload,
    previewUrl: payload,
    size: item.size,
    creator: item.creator,
  };
};

const isImageLikeAttachment = (item) => {
  if (!item) return false;
  if (item.contentType?.includes('image')) return true;
  if (item.fileTypeClassification === CoreEnums.AttachmentFileTypeClassification.Image) return true;
  return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(item.fileName || '');
};

const normalizeAttachment = (file) => {
  const isBase64 =
    file.isBase64 ?? (typeof file.content === 'string' && file.content.includes('base64,'));

  return {
    ...file,
    fileName: file.fileName || file.filename || file.name,
    downloadUrl: file.downloadUrl || file.documentTemporaryUrl,
    documentTemporaryUrl: file.documentTemporaryUrl,
    content:
      isBase64 && typeof file.content === 'string'
        ? _.split(file.content, 'base64,').pop()
        : file.content,
    isBase64,
    creator: file.creator ?? null,
  };
};

/**
 * Attachment cell for list / entity / media tables.
 * Same FileRow (dense) + FileDetailsDrawer pattern as FormBuilder table columns.
 */
const TableColAttachment = ({ item, attachments, isVisualField = false, editGridMode = false }) => {
  const list = attachments?.length ? attachments : item ? [item] : [];
  if (!list.length) return null;

  const files = list.map(normalizeAttachment);
  const showImagePreview = files.length === 1 && (isVisualField || isImageLikeAttachment(files[0]));
  const previewItem = showImagePreview ? toImagePreviewItem(files[0]) : null;

  return (
    <div className={styles.wrapper}>
      {previewItem ? <ImagePreview item={previewItem} editGridMode={editGridMode} /> : null}
      <div className={styles.rows}>
        {files.map((file) => (
          <FileRow
            key={file.id || file.fileName || file.downloadUrl || file.documentTemporaryUrl}
            {...file}
            dense
          />
        ))}
      </div>
    </div>
  );
};

export default TableColAttachment;
